/**
 * Centralised brand accessor (iter-119h).
 *
 * Three layers, applied in priority order (highest first):
 *
 *   1. Tenant SA branding  — `settings.branding.*` from /api/settings, set
 *                            via the Branding tab. White-labelling for a
 *                            specific tenant in a multi-tenant deployment.
 *   2. Build-time env vars — `REACT_APP_BRAND_*` baked at `yarn build`.
 *                            Useful when forking the codebase as a new SaaS.
 *   3. Static defaults     — current product values ("Analyzio", etc.) so
 *                            unconfigured deployments keep working.
 *
 * Usage:
 *   import { useBranding } from "@/lib/branding";
 *   const { appName, tagline, supportEmail } = useBranding();
 *
 *   // Outside React (e.g. in a service worker / non-component file):
 *   import { getStaticBranding } from "@/lib/branding";
 *   const { appName } = getStaticBranding();
 *
 * NEVER hardcode the product name in a component again — always go through
 * one of these accessors. A grep for the literal "Analyzio" should yield
 * zero hits in user-visible JSX after this iteration.
 */
import { useSettings } from "./settings-context";

const FALLBACK_NAME = "Analyzio";
const FALLBACK_TAGLINE = "Unified AI Analytics Platform";
const FALLBACK_SUPPORT_EMAIL = "support@analyzio.io";
const FALLBACK_MARKETING_URL = "https://analyzio.io";
const FALLBACK_TENANT_LABEL = "Organization";

/**
 * Pre-login org identification hints from the current URL. Used by the
 * Login / Signup pages to ask the backend which org's branding to show on a
 * SHARED login URL before any email is typed. Priority:
 *   1. explicit ?org=<slug> query param (also handy for testing)
 *   2. first sub-domain label of the host (acme.analyzio.com → "acme"),
 *      skipping generic hosts (www / app / localhost / bare domains).
 * The raw host is always sent so the backend can also match a configured
 * "Organization URL" (branding.org_url). Returns {host, slug}.
 */
export function getOrgUrlHints() {
    try {
        const host = (window.location.hostname || "").toLowerCase();
        const params = new URLSearchParams(window.location.search || "");
        let slug = (params.get("org") || "").trim().toLowerCase();
        if (!slug) {
            const labels = host.split(".");
            const generic = ["www", "app", "localhost"];
            if (labels.length >= 3 && !generic.includes(labels[0])) {
                slug = labels[0];
            }
        }
        return { host, slug };
    } catch {
        return { host: "", slug: "" };
    }
}


const STATIC_BRANDING = Object.freeze({
    appName: process.env.REACT_APP_BRAND_NAME || FALLBACK_NAME,
    tagline: process.env.REACT_APP_BRAND_TAGLINE || FALLBACK_TAGLINE,
    shortName:
        process.env.REACT_APP_BRAND_SHORT_NAME
        || process.env.REACT_APP_BRAND_NAME
        || FALLBACK_NAME,
    supportEmail: process.env.REACT_APP_SUPPORT_EMAIL || FALLBACK_SUPPORT_EMAIL,
    marketingUrl: process.env.REACT_APP_MARKETING_URL || FALLBACK_MARKETING_URL,
    tenantLabel: process.env.REACT_APP_TENANT_LABEL || FALLBACK_TENANT_LABEL,
    tenantLabelPlural:
        process.env.REACT_APP_TENANT_LABEL_PLURAL
        || `${process.env.REACT_APP_TENANT_LABEL || FALLBACK_TENANT_LABEL}s`,
    logoUrl: null,
    faviconUrl: null,
    // Copyright line — composed once so footers don't re-implement it.
    // iter-121.101 — Full copyright form: "© {year} {company}, Inc. All
    // rights reserved." Both year (auto) and `company` are deterministic
    // (no tenant SA branding override at the static layer).
    copyrightLine() {
        const yr = new Date().getFullYear();
        return `© ${yr} ${this.appName}, Inc. All rights reserved.`;
    },
});

/**
 * Synchronous accessor — safe to call outside React (theme bootstrap,
 * service workers, non-hook contexts). Returns build-time + static layer
 * only; cannot see tenant SA overrides.
 */
export function getStaticBranding() {
    return STATIC_BRANDING;
}

/**
 * Hook accessor — preferred inside components. Layers SA tenant branding
 * on top of the static defaults. Trims whitespace and falls back to the
 * underlying layer when a field is blank (so SA leaving a field empty
 * resets to platform default instead of rendering "").
 */
export function useBranding() {
    const ctx = useSettings();
    // iter-121.130 — Prefer `effective_branding` (platform→tenant merged
    // result from /api/settings) so OAs see the SA-promoted platform
    // default for any field they haven't overridden. Falls back to the
    // raw `branding` doc for back-compat (and for the SA's own session
    // where the two are identical anyway).
    const persisted = ctx?.settings?.effective_branding || ctx?.settings?.branding || {};
    const trimmed = (v) => (typeof v === "string" ? v.trim() : "");
    const pick = (saField, fallback) => trimmed(persisted[saField]) || fallback;

    const appName = pick("app_name", STATIC_BRANDING.appName);
    return {
        appName,
        // iter-121.155 — the RAW configured app name (empty string when the
        // SA hasn't filled in Branding → Brand identity → Application name).
        // Consumers that show a wordmark logo can use this to avoid printing
        // a redundant fallback name next to the logo.
        configuredAppName: trimmed(persisted.app_name),
        // `shortName` follows appName so a tenant rebrand also shrinks the
        // collapsed-sidebar mark. SA-only override via persisted.short_name.
        shortName: pick("short_name", appName),
        tagline: pick("tagline", STATIC_BRANDING.tagline),
        supportEmail: pick("support_email", STATIC_BRANDING.supportEmail),
        marketingUrl: pick("marketing_url", STATIC_BRANDING.marketingUrl),
        tenantLabel: pick("tenant_label", STATIC_BRANDING.tenantLabel),
        tenantLabelPlural: pick(
            "tenant_label_plural",
            persisted.tenant_label
                ? `${trimmed(persisted.tenant_label)}s`
                : STATIC_BRANDING.tenantLabelPlural,
        ),
        // Asset URLs — passthrough; consumer decides empty-state behaviour.
        logoUrl: persisted.logo_url || null,
        faviconUrl: persisted.favicon_url || null,
        // iter-121.101 — SA can override the legal-entity name shown in
        // the footer (e.g. "Analyzio" → "Acme Analytics, Inc.") via the
        // Login Image tab. Falls back to the product app name.
        copyrightLine: () => {
            const company = trimmed(persisted.auth_copyright_company) || appName;
            return `© ${new Date().getFullYear()} ${company}, Inc. All rights reserved.`;
        },
    };
}
